import React, { useState } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { Box } from '@mui/material';
import theme from './theme';
import Header from './components/Header';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import Chatbot from './components/Chatbot';
import ChatbotButton from './components/ChatbotButton';
import Home from './pages/Home';
import ProductDetails from './pages/ProductDetails';
import Categories from './pages/Categories';
import CategoryProducts from './pages/CategoryProducts';
import Catalogue from './pages/Catalogue';
import ContactNew from './pages/ContactNew';

// ErrorBoundary renforcé pour isoler complètement le chatbot
class ChatbotErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorCount: 0 };
  }

  static getDerivedStateFromError(error) {
    // En cas d'erreur, on masque le chatbot mais le site continue
    console.warn('[CHATBOT] Erreur capturée par ErrorBoundary:', error.message);
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Logger l'erreur sans affecter le site
    console.error('[CHATBOT ERROR BOUNDARY]', {
      error: error.message,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString()
    });
    
    this.setState(prevState => ({ errorCount: prevState.errorCount + 1 }));
    
    // Auto-reset après 60 secondes pour réessayer
    setTimeout(() => {
      console.log('[CHATBOT] Tentative de réactivation automatique...');
      this.setState({ hasError: false });
    }, 60000);
  }

  render() {
    if (this.state.hasError) {
      // Le chatbot est masqué mais le site reste 100% fonctionnel
      console.info('[CHATBOT] Temporairement désactivé (le site fonctionne normalement)');
      return null;
    }
    return this.props.children;
  }
}

function App() {
  const [isChatbotOpen, setIsChatbotOpen] = useState(false);
  const [chatbotError, setChatbotError] = useState(false);
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <ScrollToTop />
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
          <Header />
          <Box component="main" sx={{ flexGrow: 1 }}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/products/:id" element={<ProductDetails />} />
              <Route path="/categories" element={<Categories />} />
              <Route path="/catalogue" element={<Catalogue />} />
              <Route path="/categories/:categoryId/products" element={<CategoryProducts />} />
              <Route path="/contact" element={<ContactNew />} />
            </Routes>
          </Box>
          <Footer />
        </Box>
        
        {/* Chatbot avec ErrorBoundary */}
        <ChatbotErrorBoundary>
          {!chatbotError && (
            <>
              {/* Masquer le bouton quand le chatbot est ouvert */}
              {!isChatbotOpen && (
                <ChatbotButton onClick={() => setIsChatbotOpen(true)} />
              )}
              <Chatbot 
                isOpen={isChatbotOpen} 
                onClose={() => setIsChatbotOpen(false)}
                onError={() => setChatbotError(true)}
              />
            </>
          )}
        </ChatbotErrorBoundary>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
